/**
 * Filtres du planning v2 (docs/ux-v2/SPEC.md PL7, PL10, PL12, PL13). Fonctions pures.
 *
 * Les Id Mindbody de studio et de coach se recoupent d'un site à l'autre (studio 3 = dynamo Boétie sur Dynamo,
 * Monday Event sur RIISE ; coach 100000144 = deux personnes différentes, cf. StaffService::getCoaches) : toute
 * sélection est donc une clé composée « site:id ». Les types de cours aussi : « Signature » de Dynamo est défini
 * par exclusion et ne doit s'appliquer qu'aux cours de Dynamo (screens/Classes/courseTypeFilters.ts).
 */
import type { PlanningClass } from '../../api/planning';
import {
  eventStudioName,
  isEventLocation,
  routeStudioKeys,
  studioKeyOf,
  virtualStudiosOf,
  type EventSplit,
} from './eventStudios';
import { RUN_CLUB_HOME_SITE_ID, RUN_CLUB_PROGRAM_ID, RUN_CLUB_SITE_IDS } from './status';

export interface PlanningFilters {
  /**
   * Site de la marque affichée. Sélection unique (P4) ; null hors vue multi-marques (planning mono-site, mode coach) et
   * dans les pré-filtres de route (la marque du site s'applique alors). Plus de « Toutes » depuis le 07/10.
   */
  brand: string | null;
  /** Clés « site:locationId ». */
  studios: string[];
  /** Clés « site:staffId ». */
  coaches: string[];
  /** Clés « site:valeur » des types de screens/Classes/courseTypeFilters.ts. */
  types: string[];
  /** Créneaux horaires (TIME_SLOTS : matin, midi, soir), communs à toutes les marques. */
  slots: string[];
}

/** Aucune sélection, marque comprise : base des pré-filtres de route (PL13 : la marque du site s'y applique). */
export const EMPTY_FILTERS: PlanningFilters = { brand: null, studios: [], coaches: [], types: [], slots: [] };

/**
 * Créneaux horaires du filtre « Créneau » (demande de Daphné du 15/10 : « un filtre / entrée par créneaux (matin, midi,
 * soir) comme le fait EPISOD », dont le planning propose Matin, Midi, Soir sans afficher d'heures). Heure de début du
 * cours, heure locale du studio (StartDateTime de Mindbody) : matin avant 12 h, midi de 12 h à 14 h 59, après-midi de
 * 15 h à 16 h 59 (retours de Daphné du 19/10 : « pour les créneaux 15h et 17h, tu peux les passer dans un bloc
 * Après-midi »), soir à partir de 17 h. Bornes en minutes.
 */
export interface TimeSlot {
  value: string;
  label: string;
  /** Début inclus, fin exclue (minutes depuis minuit). */
  from: number;
  to: number;
}

export const TIME_SLOTS: ReadonlyArray<TimeSlot> = [
  { value: 'matin', label: 'Matin', from: 0, to: 12 * 60 },
  { value: 'midi', label: 'Midi', from: 12 * 60, to: 15 * 60 },
  { value: 'apres-midi', label: 'Après-midi', from: 15 * 60, to: 17 * 60 },
  { value: 'soir', label: 'Soir', from: 17 * 60, to: 24 * 60 },
];

/** Créneau d'un cours d'après son heure de début (« 2026-10-16T12:15:00 » → midi) ; null hors créneau ou illisible. */
export function slotOf(startDateTime: string | null | undefined): string | null {
  const match = /T(\d{2}):(\d{2})/.exec(startDateTime ?? '');
  if (!match) return null;
  const minutes = parseInt(match[1], 10) * 60 + parseInt(match[2], 10);
  return TIME_SLOTS.find((slot) => minutes >= slot.from && minutes < slot.to)?.value ?? null;
}

/**
 * Marque par défaut du filtre Marque (décision de Luc du 04/10, PLAN P4, SPEC PL7) : « de base, on est toujours sur le
 * planning de la marque du site qu'on visite ». En vue multi-marques, le site courant (s'il est parmi les sites
 * affichés) ; sinon aucune (planning mono-site, mode coach : pas de filtre Marque).
 */
export function defaultBrandOf(showBrands: boolean, siteIds: string[], currentSiteId: string): string | null {
  return showBrands && siteIds.includes(currentSiteId) ? currentSiteId : null;
}

/** Filtres à l'ouverture du planning et après « Effacer » : la marque par défaut, rien d'autre. */
export const defaultFilters = (defaultBrand: string | null): PlanningFilters => ({
  brand: defaultBrand,
  studios: [],
  coaches: [],
  types: [],
  slots: [],
});

export const siteKey = (siteId: string | number, id: string | number) => `${siteId}:${id}`;

export const siteOfKey = (key: string) => key.slice(0, key.indexOf(':'));

export const idOfKey = (key: string) => key.slice(key.indexOf(':') + 1);

/**
 * Un filtre est-il posé (bouton « Effacer ») ? Ni la marque par défaut (PL7 : elle n'est pas un choix du visiteur),
 * ni le coach figé du mode coach, ni le type figé du mode cours ne comptent ; une autre marque que celle du site, si.
 */
export function hasActiveFilters(
  filters: PlanningFilters,
  {
    lockedCoachKey = null,
    lockedTypeKey = null,
    defaultBrand = null,
  }: { lockedCoachKey?: string | null; lockedTypeKey?: string | null; defaultBrand?: string | null } = {},
): boolean {
  return (
    filters.brand !== defaultBrand ||
    filters.studios.length > 0 ||
    filters.types.some((key) => key !== lockedTypeKey) ||
    filters.slots.length > 0 ||
    filters.coaches.some((key) => key !== lockedCoachKey)
  );
}

/**
 * Choisir une marque restreint studios, coachs et cours à cette marque (PL7) : les autres sélections tombent. Les
 * créneaux, communs aux marques, restent.
 */
export function selectBrand(filters: PlanningFilters, brand: string | null): PlanningFilters {
  if (brand === null) return { ...filters, brand: null };
  const keep = (key: string) => siteOfKey(key) === brand;
  return {
    brand,
    studios: filters.studios.filter(keep),
    coaches: filters.coaches.filter(keep),
    types: filters.types.filter(keep),
    slots: filters.slots,
  };
}

export const toggleKey = (list: string[], key: string) =>
  list.includes(key) ? list.filter((k) => k !== key) : [...list, key];

export interface FilterContext {
  /** Sites affichés (multi-marques : les sites de la ville ; sinon le site courant). */
  siteIds: string[];
  /** = screens/Classes/courseTypeFilters.ts matchesCourseType, pour un type d'un site donné. */
  matchesType: (className: string, typeValue: string, siteId: string) => boolean;
  /** Coach figé du mode coach (shortcode), toujours appliqué. */
  lockedCoachKey?: string | null;
  /**
   * Studios virtuels du studio Monday Event de Paris (lib/planning/eventStudios.ts) : réglage du site qui affiche le
   * planning, appliqué aux trois marques. null ou absent : pas de séparation.
   */
  eventSplit?: EventSplit | null;
}

/**
 * Run Club (programme Mindbody 46 : « RUN CLUB » du studio Monday Event sur Dynamo Paris, et ses copies RIISE / Punch
 * Paris) : un cours de course à pied, d'aucun type du filtre « Cours ». Retours du 12/10 : « Signature » (Dynamo) et
 * « Boxe / Bootcamp » (Punch), définis par exclusion (screens/Classes/courseTypeFilters.ts), le comptaient (217 cours
 * « Signature » au lieu de 216 sur Dynamo Paris, semaine 42). Règle de la v2 seulement : la v1 n'est pas modifiée.
 * Les cours Signature ou Strong « + en option RUN CLUB » (programmes 28 et 23) restent dans leur type.
 *
 * Seulement sur les sites où le programme 46 est le Run Club (relecture du 12/10, GET site/programs : « RUN CLUB » sur
 * Dynamo, RIISE et Punch Paris) : les numéros de programme sont propres à chaque site Mindbody, et le filtre « Cours »
 * est proposé sur les 10 sites (PR 171) ; à Marseille, Bordeaux ou Annecy, un programme 46 peut être un cours Signature
 * ou Reformer, qui disparaîtrait de son type. Ailleurs, la règle par le nom (v1) s'applique telle quelle.
 */
export const RUN_CLUB_PROGRAM_SITE_IDS: ReadonlyArray<string> = [RUN_CLUB_HOME_SITE_ID, ...RUN_CLUB_SITE_IDS];

export const isRunClubProgram = (c: Pick<PlanningClass, 'ClassDescription' | 'SiteId'>) =>
  c.ClassDescription?.Program?.Id === RUN_CLUB_PROGRAM_ID && RUN_CLUB_PROGRAM_SITE_IDS.includes(String(c.SiteId));

/** Cours visibles pour ces filtres, triés par heure de début. */
export function filterClasses(classes: PlanningClass[], filters: PlanningFilters, ctx: FilterContext): PlanningClass[] {
  const sites = new Set(ctx.siteIds);
  const studios = new Set(filters.studios);
  const coaches = new Set(filters.coaches);
  const slots = new Set(filters.slots);
  const split = ctx.eventSplit ?? null;
  const typesBySite = new Map<string, string[]>();
  filters.types.forEach((key) => {
    const site = siteOfKey(key);
    typesBySite.set(site, [...(typesBySite.get(site) ?? []), idOfKey(key)]);
  });

  return classes
    .filter((c) => {
      const site = String(c.SiteId);
      // = screens/Classes : seuls les cours actifs (le serveur écarte déjà les annulés).
      if (!c.Active || c.IsCanceled) return false;
      if (!sites.has(site)) return false;
      if (filters.brand !== null && site !== filters.brand) return false;
      if (studios.size && !studios.has(studioKeyOf(c, split))) return false;
      if (ctx.lockedCoachKey && siteKey(site, c.Staff?.Id) !== ctx.lockedCoachKey) return false;
      if (coaches.size && !coaches.has(siteKey(site, c.Staff?.Id))) return false;
      if (slots.size && !slots.has(slotOf(c.StartDateTime) ?? '')) return false;
      if (filters.types.length) {
        if (isRunClubProgram(c)) return false;
        const values = typesBySite.get(site);
        if (!values || !values.some((value) => ctx.matchesType(c.ClassDescription?.Name ?? '', value, site)))
          return false;
      }
      return true;
    })
    .sort((a, b) => a.StartDateTime.localeCompare(b.StartDateTime));
}

/* -------------------------------------------------------------- options des listes */

export interface FilterOption {
  /** Clé composée « site:id ». */
  value: string;
  label: string;
  siteId: string;
}

export interface FilterOptionGroup {
  siteId: string;
  label: string;
  options: FilterOption[];
}

/** Sites dont on propose les options : la marque choisie, sinon tous les sites affichés. */
const optionSites = (siteIds: string[], brand: string | null) =>
  brand === null ? siteIds : siteIds.filter((s) => s === brand);

export interface LocationLike {
  Id: number;
  SiteID: number | string;
  Name: string;
}

/**
 * = screens/Classes/components/Filters/Studios.tsx : sur Dynamo Paris, le studio Id 9 (« Monday Corporate
 * Sales ») n'est pas proposé. Règle métier conservée (SPEC § 3.6, PL13), appliquée au site Dynamo Paris quel que soit
 * le site qui affiche le planning multi-marques.
 */
export const HIDDEN_STUDIOS: ReadonlyArray<string> = [siteKey('5736526', 9)];

/**
 * Studios (clés « site:locationId ») qui ont au moins un cours du type choisi dans la semaine chargée ; null sans type
 * choisi (la liste des studios ne change pas). Recette de Ségolène (RIISE Paris, 28/10), décision de Luc : « en filtre
 * Yoga, pourquoi les studios qui ne proposent pas du yoga ne disparaissent-ils pas de la liste ? ». Seuls la marque et
 * le type comptent : ni le studio, ni le coach, ni le créneau déjà choisis ne réduisent la liste.
 */
export function studiosWithTypes(
  classes: PlanningClass[] | undefined,
  filters: PlanningFilters,
  ctx: FilterContext,
): Set<string> | null {
  if (!filters.types.length) return null;
  const typed = filterClasses(classes ?? [], { ...filters, studios: [], coaches: [], slots: [] }, { ...ctx, lockedCoachKey: null });
  return new Set(typed.map((c) => studioKeyOf(c, ctx.eventSplit ?? null)));
}

/**
 * Coachs (clés « site:staffId ») qui donnent au moins un cours du type et dans le studio choisis, dans la semaine
 * chargée ; null sans type ni studio choisi (la liste des coachs ne change pas). Demande de Daphné (RIISE Paris, 30/10),
 * suite du filtre des studios : « quand on choisit un type de cours, ou un studio, la liste des coachs ne propose plus
 * que ceux qui enseignent dans le studio sélectionné ou qui enseignent la discipline sélectionnée ». Seuls la marque, le
 * type et le studio comptent : ni le coach, ni le créneau déjà choisis ne réduisent la liste.
 */
export function coachesWithFilters(
  classes: PlanningClass[] | undefined,
  filters: PlanningFilters,
  ctx: FilterContext,
): Set<string> | null {
  if (!filters.types.length && !filters.studios.length) return null;
  const matching = filterClasses(classes ?? [], { ...filters, coaches: [], slots: [] }, { ...ctx, lockedCoachKey: null });
  return new Set(matching.filter((c) => c.Staff?.Id != null).map((c) => siteKey(String(c.SiteId), c.Staff?.Id)));
}

/**
 * Studios (clés « site:id », studios virtuels compris) qui ont au moins un cours visible (actif, non annulé) parmi
 * `classes` : l'entrée « Monday Event » d'origine n'est proposée, avec la séparation active, que si elle en a.
 */
export function studiosWithClasses(classes: PlanningClass[] | undefined, split: EventSplit | null): Set<string> {
  return new Set((classes ?? []).filter((c) => c.Active && !c.IsCanceled).map((c) => studioKeyOf(c, split)));
}

/** Options d'une location : elle-même, ou, avec la séparation active, ses studios virtuels (+ elle-même si elle a des cours). */
function locationOptions(
  location: LocationLike,
  siteId: string,
  shortName: (locationName: string) => string,
  split: EventSplit | null,
  present: Set<string> | null,
): FilterOption[] {
  const own = { value: siteKey(siteId, location.Id), label: shortName(location.Name).trim(), siteId };
  if (!split || !isEventLocation(siteId, location.Id)) return [own];
  const virtual = virtualStudiosOf(siteId, split).map((s) => ({ value: siteKey(siteId, s.id), label: s.label, siteId }));
  return present?.has(own.value) ? [...virtual, own] : virtual;
}

/**
 * Studios (site/locations) des marques affichées, groupés par marque, dans l'ordre des sites. `only` (studiosWithTypes) :
 * seulement ces studios, plus ceux déjà choisis (une sélection ne disparaît pas de la liste).
 */
export function studioGroups(
  locations: LocationLike[] | undefined,
  siteIds: string[],
  brand: string | null,
  labelOf: (siteId: string) => string,
  shortName: (locationName: string) => string,
  only: Set<string> | null = null,
  selected: string[] = [],
  events: { split: EventSplit | null; present: Set<string> | null } = { split: null, present: null },
): FilterOptionGroup[] {
  const list = Array.isArray(locations) ? locations : [];
  const offered = (key: string) => only === null || only.has(key) || selected.includes(key);
  // Entrée sélectionnée (route `#/studio/10`…) : toujours dans la liste, même sans cours.
  const present = new Set([...(events.present ?? []), ...selected]);
  return optionSites(siteIds, brand)
    .map((siteId) => ({
      siteId,
      label: labelOf(siteId),
      options: list
        .filter((l) => String(l.SiteID) === siteId && !HIDDEN_STUDIOS.includes(siteKey(siteId, l.Id)))
        .flatMap((l) => locationOptions(l, siteId, shortName, events.split, present))
        .filter((o) => offered(o.value))
        .sort((a, b) => a.label.localeCompare(b.label, 'fr')),
    }))
    .filter((group) => group.options.length > 0);
}

export interface CoachLike {
  Id: number;
  SiteId: number | string;
  FirstName: string;
}

/**
 * Coachs (staff/coaches : masqués du BO déjà exclus par le serveur) des marques affichées, groupés par marque. `only`
 * (coachesWithFilters) : seulement ces coachs, plus ceux déjà choisis (une sélection ne disparaît pas de la liste).
 */
export function coachGroups(
  coaches: CoachLike[] | undefined,
  siteIds: string[],
  brand: string | null,
  labelOf: (siteId: string) => string,
  only: Set<string> | null = null,
  selected: string[] = [],
): FilterOptionGroup[] {
  const list = Array.isArray(coaches) ? coaches : [];
  const offered = (key: string) => only === null || only.has(key) || selected.includes(key);
  return optionSites(siteIds, brand)
    .map((siteId) => ({
      siteId,
      label: labelOf(siteId),
      options: list
        .filter((c) => String(c.SiteId) === siteId)
        .map((c) => ({ value: siteKey(siteId, c.Id), label: (c.FirstName ?? '').trim(), siteId }))
        .filter((o) => o.label !== '' && offered(o.value))
        .sort((a, b) => a.label.localeCompare(b.label, 'fr')),
    }))
    .filter((group) => group.options.length > 0);
}

export interface ClassNamesLike {
  SiteId: number | string;
  Location?: { Id?: number | string | null; Name?: string | null } | null;
  Staff?: { Id?: number | string | null; FirstName?: string | null } | null;
  ClassDescription?: { Program?: { Id?: number | string | null } | null } | null;
}

/**
 * Noms des studios et des coachs connus par les cours déjà chargés (clés « site:id ») : résumé des listes et sous-titre
 * d'une route à pré-filtre (#/studio/4, #/coach/50019) tant que site/locations et staff/coaches ne sont pas arrivés
 * (revue du 05/10 : « 1 studios » puis « Oberkampf » 1,7 s plus tard).
 */
export function namesFromClasses(
  classes: ClassNamesLike[] | undefined,
  shortName: (locationName: string) => string,
  split: EventSplit | null = null,
): { studios: Map<string, string>; coaches: Map<string, string> } {
  const studios = new Map<string, string>();
  const coaches = new Map<string, string>();
  (classes ?? []).forEach((c) => {
    const site = String(c.SiteId);
    const location = c.Location;
    const virtual = eventStudioName(c, split);
    if (location?.Id != null && virtual) studios.set(studioKeyOf(c, split), virtual);
    else if (location?.Id != null && location.Name) studios.set(siteKey(site, location.Id), shortName(location.Name).trim());
    const staff = c.Staff;
    if (staff?.Id != null && (staff.FirstName ?? '').trim()) coaches.set(siteKey(site, staff.Id), (staff.FirstName ?? '').trim());
  });
  return { studios, coaches };
}

export interface CourseTypeLike {
  value: string;
  label: string;
}

/** Étiquettes « Cours » : types configurés pour chaque site affiché (ou la marque choisie). */
export function courseTypeOptions(
  siteIds: string[],
  brand: string | null,
  optionsOf: (siteId: string) => CourseTypeLike[],
): FilterOption[] {
  return optionSites(siteIds, brand).flatMap((siteId) =>
    optionsOf(siteId).map((type) => ({ value: siteKey(siteId, type.value), label: type.label, siteId })),
  );
}

/* ------------------------------------------------------------ pré-filtres des routes */

export interface RoutePrefilter {
  filters: PlanningFilters;
  /** Semaine ISO demandée par `studio/:id/week/:n`. */
  week: number | null;
}

/**
 * Pré-filtres des routes publiques du planning (SPEC § 3.1, PL13), toujours relatifs au site courant (ce sont les
 * Id du site dans les menus des thèmes et les e-mails) : `studio/:id`, `studio/:id/week/:n`, `coach/:id`,
 * `cours/:type`, `studio/:id/cours/:type`. Index (`/`) : null (rien à appliquer).
 * Un type de cours inconnu du site est ignoré (= Filters/CourseTypeFilter.tsx).
 * Studios virtuels du studio Monday Event de Paris (lib/planning/eventStudios.ts) : `studio/10-46` (Run Club),
 * `studio/10-42` (Special Events), avec `/week/:n` et `/cours/:type` comme les autres ; `studio/10` garde tous ses cours.
 */
export function prefilterFromPath(
  pathname: string,
  currentSiteId: string,
  courseTypeValues: string[],
  split: EventSplit | null = null,
): RoutePrefilter | null {
  const path = pathname.replace(/\/+$/, '');
  const studio = /^\/studio\/(\d+(?:-\d+)?)(?:\/week\/(\d+))?(?:\/cours\/([^/]+))?$/.exec(path);
  const coach = /^\/coach\/(\d+)$/.exec(path);
  const cours = /^\/cours\/([^/]+)$/.exec(path);
  if (!studio && !coach && !cours) return null;

  const filters: PlanningFilters = { ...EMPTY_FILTERS, studios: [], coaches: [], types: [], slots: [] };
  let week: number | null = null;
  let type: string | null = null;

  if (studio) {
    filters.studios = routeStudioKeys(currentSiteId, studio[1], split);
    const n = studio[2] ? parseInt(studio[2], 10) : NaN;
    week = n >= 1 && n <= 53 ? n : null;
    type = studio[3] ? decodeURIComponent(studio[3]) : null;
  } else if (coach) {
    filters.coaches = [siteKey(currentSiteId, parseInt(coach[1], 10))];
  } else if (cours) {
    type = decodeURIComponent(cours[1]);
  }
  if (type && courseTypeValues.includes(type)) filters.types = [siteKey(currentSiteId, type)];

  return { filters, week };
}
